import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';

interface ReportCard {
  title: string;
  desc: string;
  icon: string;
  color: string;
  path: string;
  badge?: string;
}

const inventoryReports: ReportCard[] = [
  { title: 'Stock Summary', desc: 'Current stock levels, valuation, and status for all items', icon: 'ri-stack-line', color: 'bg-indigo-50 text-[#4f46e5]', path: '/reports/stock-summary' },
  { title: 'Stock Ledger', desc: 'Complete movement history for any item in a warehouse', icon: 'ri-file-list-3-line', color: 'bg-blue-50 text-blue-600', path: '/reports/stock-ledger' },
  { title: 'Low Stock Alert', desc: 'Items below minimum reorder level with suggested reorder quantity', icon: 'ri-alert-line', color: 'bg-red-50 text-red-500', path: '/reports/low-stock', badge: 'Action Required' },
];

const purchaseReports: ReportCard[] = [
  { title: 'Purchase Register', desc: 'All purchase invoices with GST breakdown by date range', icon: 'ri-store-2-line', color: 'bg-green-50 text-green-600', path: '/reports/purchase-register' },
  { title: 'GST Purchase Register', desc: 'Purchase taxable values by GST slab — GSTR-2A/2B reconciliation', icon: 'ri-receipt-line', color: 'bg-teal-50 text-teal-600', path: '/reports/gst-purchase', badge: 'GST Filing' },
];

const salesReports: ReportCard[] = [
  { title: 'Sales Register', desc: 'All sales invoices with GST breakdown, payment status by date range', icon: 'ri-bar-chart-2-line', color: 'bg-emerald-50 text-emerald-600', path: '/reports/sales-register' },
  { title: 'GST Sales Register (GSTR-1)', desc: 'Sales taxable values by GST slab — GSTR-1 filing preparation', icon: 'ri-file-shield-2-line', color: 'bg-amber-50 text-amber-600', path: '/reports/gst-sales', badge: 'GST Filing' },
  { title: 'Outstanding Invoices', desc: 'Unpaid invoices with aging buckets (0-30, 31-60, 61-90, 90+)', icon: 'ri-money-rupee-circle-line', color: 'bg-orange-50 text-orange-600', path: '/reports/outstanding' },
];

const generalReports: ReportCard[] = [
  { title: 'Day Book', desc: 'All transactions on a single date grouped by type with subtotals', icon: 'ri-calendar-check-line', color: 'bg-violet-50 text-violet-600', path: '/reports/day-book' },
  { title: 'Party Ledger', desc: 'Customer or supplier ledger with opening balance, transactions, and running balance', icon: 'ri-group-line', color: 'bg-slate-100 text-slate-600', path: '/reports/party-ledger' },
];

const manufacturingReports: ReportCard[] = [
  { title: 'Manufacturing Reports', desc: 'Production summary, material consumption vs BOM variance, and quick links', icon: 'ri-settings-3-line', color: 'bg-indigo-50 text-indigo-600', path: '/reports/manufacturing', badge: 'Production' },
  { title: 'Cost Center Report', desc: 'Department-wise labour, material and overhead cost vs budget, utilization, output and downtime by work center', icon: 'ri-building-4-line', color: 'bg-emerald-50 text-emerald-600', path: '/reports/cost-centers', badge: 'Departments' },
  { title: 'Scrap Report', desc: 'Scrap by production order, stage, item and reason — yield, P&L loss and recovery from scrap sales', icon: 'ri-delete-bin-6-line', color: 'bg-rose-50 text-rose-600', path: '/reports/scrap', badge: 'Scrap' },
];

const financialReports: ReportCard[] = [
  { title: 'Financial Reports', desc: 'Profit & Loss, Balance Sheet, General Ledger, Trial Balance, and Forex', icon: 'ri-funds-line', color: 'bg-blue-50 text-blue-600', path: '/reports/financial', badge: 'Financials' },
];

const gstReports: ReportCard[] = [
  { title: 'GST Reports', desc: 'GSTR-1, GSTR-2B, GSTR-3B monthly return filing summary and ITC reconciliation', icon: 'ri-file-shield-line', color: 'bg-emerald-50 text-emerald-600', path: '/reports/gst', badge: 'GST Filing' },
];

const analyticsReports: ReportCard[] = [
  { title: 'Executive Dashboard', desc: 'Real-time revenue trends, production status, inventory health and alerts', icon: 'ri-dashboard-3-line', color: 'bg-purple-50 text-purple-600', path: '/reports/dashboard', badge: 'Executive' },
  { title: 'AI Demand Forecasting', desc: 'Machine learning demand predictions, sales trend backtesting and replenishment', icon: 'ri-magic-line', color: 'bg-cyan-50 text-cyan-600', path: '/reports/ai-forecasting', badge: 'AI Powered' },
];

interface SectionProps {
  title: string;
  icon: string;
  cards: ReportCard[];
}

function ReportSection({ title, icon, cards }: SectionProps) {
  const navigate = useNavigate();
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <i className={`${icon} text-base text-[#64748b]`} />
        <h3 className="text-sm font-semibold text-[#64748b] uppercase tracking-wider">{title}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((r) => (
          <button
            key={r.path}
            onClick={() => navigate(r.path)}
            className="bg-white border border-[#e2e8f0] rounded-xl p-5 hover:border-[#4f46e5]/40 hover:bg-indigo-50/20 transition-all cursor-pointer text-left group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-9 h-9 rounded-lg ${r.color.split(' ')[0]} flex items-center justify-center`}>
                <i className={`${r.icon} text-base ${r.color.split(' ')[1]}`} />
              </div>
              {r.badge && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                  r.badge === 'Action Required' ? 'bg-red-100 text-red-600' :
                  r.badge === 'AI Powered' ? 'bg-cyan-100 text-cyan-700' :
                  r.badge === 'Executive' ? 'bg-purple-100 text-purple-700' :
                  r.badge === 'Production' ? 'bg-indigo-100 text-indigo-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {r.badge}
                </span>
              )}
            </div>
            <h3 className="text-sm font-semibold text-[#1e293b] group-hover:text-[#4f46e5] transition-colors">{r.title}</h3>
            <p className="text-xs text-[#64748b] mt-1 leading-relaxed">{r.desc}</p>
            <div className="mt-4 flex items-center gap-1 text-xs text-[#4f46e5] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              <span>Open Report</span>
              <i className="ri-arrow-right-line text-xs" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ReportsLanding() {
  const totalCount =
    inventoryReports.length +
    purchaseReports.length +
    salesReports.length +
    generalReports.length +
    manufacturingReports.length +
    financialReports.length +
    gstReports.length +
    analyticsReports.length;

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-[#1e293b]">Reports</h2>
            <p className="text-sm text-[#64748b] mt-0.5">
              {totalCount} reports available · All support Excel, PDF & Print export
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">
            <i className="ri-checkbox-circle-line" />
            <span>Connected to General Ledger & Real-Time Financials</span>
          </div>
        </div>

        <ReportSection title="Manufacturing Reports" icon="ri-settings-3-line" cards={manufacturingReports} />
        <ReportSection title="Financial & COA Reports" icon="ri-funds-line" cards={financialReports} />
        <ReportSection title="GST & Taxation Reports" icon="ri-file-shield-line" cards={gstReports} />
        <ReportSection title="Executive & AI Forecasting" icon="ri-magic-line" cards={analyticsReports} />
        <ReportSection title="Inventory Reports" icon="ri-archive-stack-line" cards={inventoryReports} />
        <ReportSection title="Purchase Reports" icon="ri-store-2-line" cards={purchaseReports} />
        <ReportSection title="Sales Reports" icon="ri-shopping-cart-2-line" cards={salesReports} />
        <ReportSection title="General Reports" icon="ri-file-chart-line" cards={generalReports} />
      </div>
    </AppLayout>
  );
}
